import {
	Action,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	type Establishment,
} from "@pilatestime/shared-types";
import { createFileRoute, useParams } from "@tanstack/react-router";
import {
	Building2,
	Edit,
	Mail,
	MapPin,
	Phone,
	Plus,
	Search,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	useCreateEstablishment,
	useDeleteEstablishment,
	useUpdateEstablishment,
} from "@/api/establishments";
import type { PaginationMeta } from "@/components/data-table";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { EstablishmentList } from "./-components/EstablishmentList";

const searchSchema = z.object({
	page: z.number().catch(DEFAULT_PAGE),
	limit: z.number().catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/establishments/",
)({
	validateSearch: searchSchema,
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		return { userId, token, orgId };
	},
	component: EstablishmentsPage,
});

function EstablishmentsPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/establishments/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 500); // 500ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const can = useCan();
	const canManageEstablishments = can(Action.MANAGE_ESTABLISHMENTS);

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	// State for dialogs and forms
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [editingEstablishment, setEditingEstablishment] =
		useState<Establishment | null>(null);

	// Form state
	const [formData, setFormData] = useState({
		name: "",
		description: "",
		address: "",
		phone: "",
		email: "",
	});

	// Mutations
	const createEstablishmentMutation = useCreateEstablishment();
	const updateEstablishmentMutation = useUpdateEstablishment();
	const deleteEstablishmentMutation = useDeleteEstablishment();

	const resetForm = () => {
		setFormData({
			name: "",
			description: "",
			address: "",
			phone: "",
			email: "",
		});
	};

	const handleCreateEstablishment = async () => {
		if (!formData.name.trim()) {
			// Show validation error - this is not handled by mutation
			return;
		}

		try {
			await createEstablishmentMutation.mutateAsync({
				token,
				organizationId: orgId,
				data: {
					name: formData.name.trim(),
					description: formData.description.trim() || undefined,
					address: formData.address.trim() || undefined,
					phone: formData.phone.trim() || undefined,
					email: formData.email.trim() || undefined,
				},
			});

			// Success toast is handled by the mutation hook
			setIsCreateDialogOpen(false);
			resetForm();
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Failed to create establishment:", error);
		}
	};

	const handleEditEstablishment = (establishment: Establishment) => {
		setEditingEstablishment(establishment);
		setFormData({
			name: establishment.name,
			description: establishment.description || "",
			address: establishment.address || "",
			phone: establishment.phone || "",
			email: establishment.email || "",
		});
	};

	const handleUpdateEstablishment = async () => {
		if (!editingEstablishment || !formData.name.trim()) {
			// Show validation error - this is not handled by mutation
			return;
		}

		try {
			await updateEstablishmentMutation.mutateAsync({
				token,
				organizationId: orgId,
				establishmentId: editingEstablishment.id,
				data: {
					name: formData.name.trim(),
					description: formData.description.trim() || undefined,
					address: formData.address.trim() || undefined,
					phone: formData.phone.trim() || undefined,
					email: formData.email.trim() || undefined,
				},
			});

			// Success toast is handled by the mutation hook
			setEditingEstablishment(null);
			resetForm();
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Failed to update establishment:", error);
		}
	};

	const isLoading =
		createEstablishmentMutation.isPending ||
		updateEstablishmentMutation.isPending ||
		deleteEstablishmentMutation.isPending;

	return (
		<div
			className="h-full flex flex-col space-y-3"
			data-testid="establishments-page"
		>
			{/* Header */}
			<PageHeader
				title={t("establishments.management.title")}
				description={t("establishments.management.description")}
				showBackButton={false}
			/>
			{/* Establishments Card */}
			<Card className="flex-1 flex flex-col overflow-hidden">
				<CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
					{/* Search */}
					<div className="relative w-full sm:w-72">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("establishments.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="pl-10 pr-10"
							data-testid="establishments-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						{searchTerm !== debouncedSearchTerm && (
							<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							</div>
						)}
					</div>

					{/* Create Button */}
					{canManageEstablishments && (
						<Dialog
							open={isCreateDialogOpen}
							onOpenChange={setIsCreateDialogOpen}
						>
							<DialogTrigger asChild>
								<Button
									className="btn btn-primary text-primary-content w-full sm:w-auto"
									data-testid="create-establishment-button"
								>
									<Plus className="h-4 w-4 mr-2" />
									{t("establishments.management.createNew")}
								</Button>
							</DialogTrigger>
							<DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
								<DialogHeader className="space-y-3 pb-6 border-b border-base-300">
									<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
										<Building2 className="h-5 w-5 text-primary" />
										{t("establishments.management.createNew")}
									</DialogTitle>
									<DialogDescription className="text-base-content/70">
										{t("establishments.management.description")}
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-6 py-6">
									{/* Basic Information Section */}
									<div className="space-y-4">
										<div className="flex items-center gap-2 pb-2 border-b border-base-200">
											<div className="w-2 h-2 bg-primary rounded-full" />
											<h3 className="text-lg font-medium text-base-content">
												{t("establishments.management.form.sections.basicInfo")}
											</h3>
										</div>
										<div className="grid gap-4">
											<div className="space-y-2">
												<Label
													htmlFor="name"
													className="text-sm font-medium text-base-content"
												>
													{t("establishments.management.form.name")}
													<span className="text-error ml-1">*</span>
												</Label>
												<Input
													id="name"
													value={formData.name}
													onChange={(e) =>
														setFormData({ ...formData, name: e.target.value })
													}
													placeholder={t(
														"establishments.management.form.namePlaceholder",
													)}
													className="h-10"
												/>
											</div>
											<div className="space-y-2">
												<Label
													htmlFor="description"
													className="text-sm font-medium text-base-content"
												>
													{t("establishments.management.form.description")}
												</Label>
												<Textarea
													id="description"
													value={formData.description}
													onChange={(e) =>
														setFormData({
															...formData,
															description: e.target.value,
														})
													}
													placeholder={t(
														"establishments.management.form.descriptionPlaceholder",
													)}
													className="min-h-[80px] resize-none"
												/>
											</div>
										</div>
									</div>
									{/* Location Section */}
									<div className="space-y-4">
										<div className="flex items-center gap-2 pb-2 border-b border-base-200">
											<div className="w-2 h-2 bg-primary rounded-full" />
											<h3 className="text-lg font-medium text-base-content">
												{t("establishments.management.form.sections.location")}
											</h3>
										</div>
										<div className="space-y-2">
											<Label
												htmlFor="address"
												className="text-sm font-medium text-base-content"
											>
												{t("establishments.management.form.address")}
											</Label>
											<div className="relative">
												<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
												<Input
													id="address"
													value={formData.address}
													onChange={(e) =>
														setFormData({
															...formData,
															address: e.target.value,
														})
													}
													placeholder={t(
														"establishments.management.form.addressPlaceholder",
													)}
													className="pl-10 h-10"
												/>
											</div>
										</div>
									</div>

									{/* Contact Information Section */}
									<div className="space-y-4">
										<div className="flex items-center gap-2 pb-2 border-b border-base-200">
											<div className="w-2 h-2 bg-primary rounded-full" />
											<h3 className="text-lg font-medium text-base-content">
												{t("establishments.management.form.sections.contact")}
											</h3>
										</div>
										<div className="grid gap-4">
											<div className="space-y-2">
												<Label
													htmlFor="phone"
													className="text-sm font-medium text-base-content"
												>
													{t("establishments.management.form.phone")}
												</Label>
												<div className="relative">
													<Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
													<Input
														id="phone"
														value={formData.phone}
														onChange={(e) =>
															setFormData({
																...formData,
																phone: e.target.value,
															})
														}
														placeholder={t(
															"establishments.management.form.phonePlaceholder",
														)}
														className="pl-10 h-10"
													/>
												</div>
											</div>
											<div className="space-y-2">
												<Label
													htmlFor="email"
													className="text-sm font-medium text-base-content"
												>
													{t("establishments.management.form.email")}
												</Label>
												<div className="relative">
													<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
													<Input
														id="email"
														type="email"
														value={formData.email}
														onChange={(e) =>
															setFormData({
																...formData,
																email: e.target.value,
															})
														}
														placeholder={t(
															"establishments.management.form.emailPlaceholder",
														)}
														className="pl-10 h-10"
													/>
												</div>
											</div>
										</div>
									</div>
								</div>
								<DialogFooter className="flex gap-3 pt-6 border-t border-base-300">
									<button
										type="button"
										className="btn btn-outline flex-1 h-11"
										onClick={() => {
											setIsCreateDialogOpen(false);
											resetForm();
										}}
										disabled={isLoading}
									>
										{t("common.cancel")}
									</button>
									<button
										type="button"
										className="btn btn-primary flex-1 h-11"
										onClick={handleCreateEstablishment}
										disabled={isLoading || !formData.name.trim()}
									>
										{isLoading ? t("common.creating") : t("common.create")}
									</button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					)}
				</CardHeader>

				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
					{/* Pagination */}
					<div className="flex-shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>

					{/* Table */}
					<div className="flex-1 overflow-hidden">
						<EstablishmentList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm || undefined}
							userId={userId}
							token={token}
							canManageEstablishments={canManageEstablishments || false}
							onPaginationMetaChange={handlePaginationMetaChange}
							onEdit={handleEditEstablishment}
						/>
					</div>
				</CardContent>
			</Card>

			{/* Edit Dialog */}
			<Dialog
				open={!!editingEstablishment}
				onOpenChange={(open) => {
					if (!open) {
						setEditingEstablishment(null);
						resetForm();
					}
				}}
			>
				<DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
					<DialogHeader className="space-y-3 pb-6 border-b border-base-300">
						<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
							<Edit className="h-5 w-5 text-primary" />
							{t("establishments.management.form.update")}
						</DialogTitle>
						<DialogDescription className="text-base-content/70">
							{t("establishments.management.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-6 py-6">
						{/* Basic Information Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("establishments.management.form.sections.basicInfo")}
								</h3>
							</div>
							<div className="grid gap-4">
								<div className="space-y-2">
									<Label
										htmlFor="edit-name"
										className="text-sm font-medium text-base-content"
									>
										{t("establishments.management.form.name")}
										<span className="text-error ml-1">*</span>
									</Label>
									<Input
										id="edit-name"
										value={formData.name}
										onChange={(e) =>
											setFormData({ ...formData, name: e.target.value })
										}
										placeholder={t(
											"establishments.management.form.namePlaceholder",
										)}
										className="h-10"
									/>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="edit-description"
										className="text-sm font-medium text-base-content"
									>
										{t("establishments.management.form.description")}
									</Label>
									<Textarea
										id="edit-description"
										value={formData.description}
										onChange={(e) =>
											setFormData({ ...formData, description: e.target.value })
										}
										placeholder={t(
											"establishments.management.form.descriptionPlaceholder",
										)}
										className="min-h-[80px] resize-none"
									/>
								</div>
							</div>
						</div>

						{/* Location Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("establishments.management.form.sections.location")}
								</h3>
							</div>
							<div className="space-y-2">
								<Label
									htmlFor="edit-address"
									className="text-sm font-medium text-base-content"
								>
									{t("establishments.management.form.address")}
								</Label>
								<div className="relative">
									<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
									<Input
										id="edit-address"
										value={formData.address}
										onChange={(e) =>
											setFormData({ ...formData, address: e.target.value })
										}
										placeholder={t(
											"establishments.management.form.addressPlaceholder",
										)}
										className="pl-10 h-10"
									/>
								</div>
							</div>
						</div>

						{/* Contact Information Section */}
						<div className="space-y-4">
							<div className="flex items-center gap-2 pb-2 border-b border-base-200">
								<div className="w-2 h-2 bg-primary rounded-full" />
								<h3 className="text-lg font-medium text-base-content">
									{t("establishments.management.form.sections.contact")}
								</h3>
							</div>
							<div className="grid gap-4">
								<div className="space-y-2">
									<Label
										htmlFor="edit-phone"
										className="text-sm font-medium text-base-content"
									>
										{t("establishments.management.form.phone")}
									</Label>
									<div className="relative">
										<Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
										<Input
											id="edit-phone"
											value={formData.phone}
											onChange={(e) =>
												setFormData({ ...formData, phone: e.target.value })
											}
											placeholder={t(
												"establishments.management.form.phonePlaceholder",
											)}
											className="pl-10 h-10"
										/>
									</div>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="edit-email"
										className="text-sm font-medium text-base-content"
									>
										{t("establishments.management.form.email")}
									</Label>
									<div className="relative">
										<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
										<Input
											id="edit-email"
											type="email"
											value={formData.email}
											onChange={(e) =>
												setFormData({ ...formData, email: e.target.value })
											}
											placeholder={t(
												"establishments.management.form.emailPlaceholder",
											)}
											className="pl-10 h-10"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
					<DialogFooter className="flex gap-3 pt-6 border-t border-base-300">
						<button
							type="button"
							className="btn btn-outline flex-1 h-11"
							onClick={() => {
								setEditingEstablishment(null);
								resetForm();
							}}
							disabled={isLoading}
						>
							{t("common.cancel")}
						</button>
						<button
							type="button"
							className="btn btn-primary flex-1 h-11"
							onClick={handleUpdateEstablishment}
							disabled={isLoading || !formData.name.trim()}
						>
							{isLoading ? t("common.updating") : t("common.update")}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
